import React, { FC, useEffect, useRef, useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import { useDispatch, useSelector } from 'react-redux'
import Checkbox from 'views/components/atoms/Checkbox/Checkbox'
import FormField from 'views/components/atoms/FormField/FormField'
import TextField from 'views/components/atoms/TextField/TextField'
import TextInput from 'views/components/atoms/TextField/TextInput'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import DimmerLoader from 'views/components/molecules/DimmerLoader/DimmerLoader'
import Modal from 'views/components/molecules/Modal/Modal'
import { apiStateSelectors } from 'modules/api/apiState'
import { tpnApiEvents, tpnApiSelectors } from 'modules/api/tpn'
import styles from './TpnSettingsModal.module.scss'

const validationSchema = yup.object().shape({
  username: yup.string().trim().required('TPN username is required'),
  password: yup.string().required('Password is required'),
})

type Props = {
  isOpen: boolean
  onClose(): void
}

type View = 'connect' | 'confirmDisconnect'

/** Fires `onDone` on the first true -> false transition of `loading`. */
const useOnSettled = (loading: boolean, onDone: () => void) => {
  const wasLoading = useRef(false)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone
  useEffect(() => {
    if (wasLoading.current && !loading) {
      onDoneRef.current()
    }
    wasLoading.current = loading
  }, [loading])
}

/**
 * Opened from the TPN card's switch: the connect form when nothing is linked,
 * otherwise a confirmation to disconnect. Send now lives on the card.
 *
 * The connect password goes to TPN for verification and is never stored, so
 * this is separate from the "TPN Connect" credit-check login elsewhere on the
 * integrations page.
 */
const TpnSettingsModal: FC<Props> = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()

  const settings = useSelector(tpnApiSelectors.getTpnSettings)
  const isConnected = useSelector(tpnApiSelectors.isTpnConnected)
  const saveError = useSelector(tpnApiSelectors.getTpnSaveError)
  const isSaving = useSelector(state => apiStateSelectors.isLoading(state)([tpnApiEvents.saveTpnSettings_request]))
  const isRemoving = useSelector(state => apiStateSelectors.isLoading(state)([tpnApiEvents.removeTpnSettings_request]))
  const isRefetching = useSelector(state => apiStateSelectors.isLoading(state)([tpnApiEvents.tpnSettings_request]))
  // A save only reports success; the settings arrive with the refetch that
  // follows, so the connect form stays busy until that lands too.
  const isConnecting = isSaving || isRefetching
  const busy = isConnecting || isRemoving

  const [view, setView] = useState<View>(isConnected ? 'confirmDisconnect' : 'connect')

  // Pick the view when the modal opens and hold it until it closes, so a
  // successful connect doesn't flash the disconnect confirmation before the
  // modal closes.
  useEffect(() => {
    if (isOpen) {
      setView(isConnected ? 'confirmDisconnect' : 'connect')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Close once the connection shows up. Keyed to `isConnected` rather than
  // `isSaving` settling, because the save succeeds before the refetch fills in
  // the settings. A failed connect stays open with the error.
  useEffect(() => {
    if (isOpen && view === 'connect' && isConnected) onClose()
  }, [isOpen, view, isConnected, onClose])
  useOnSettled(isRemoving, () => {
    if (!isConnected) onClose()
  })

  const handleClose = () => {
    if (busy) return
    dispatch(tpnApiEvents.clearTpnSaveError())
    onClose()
  }

  // The form lives inside the modal body, so it unmounts (and resets) whenever
  // the modal closes.
  const renderConnect = () => (
    <Formik
      initialValues={{ username: '', password: '', autoSend: true }}
      validationSchema={validationSchema}
      validateOnBlur={false}
      onSubmit={values => {
        dispatch(
          tpnApiEvents.saveTpnSettings_request({
            username: values.username.trim(),
            password: values.password,
            autoSend: values.autoSend,
          }),
        )
      }}
    >
      {({ handleSubmit, handleChange, values, errors, submitCount }) => {
        // Validation errors only show once the user has tried to submit;
        // after that they track edits live.
        const visibleErrors = submitCount > 0 ? errors : {}
        // Editing a field clears the API error so it doesn't linger.
        const onFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          if (saveError) dispatch(tpnApiEvents.clearTpnSaveError())
          handleChange(e)
        }

        return (
          <Modal.Body
            actions={
              isConnecting
                ? []
                : [
                    { label: 'Enable', onClick: () => handleSubmit() },
                    { label: 'Close', onClick: handleClose },
                  ]
            }
          >
            <DimmerLoader loading={isConnecting} fadeStyle="flat" bgColor="white">
              <p className={styles.help}>Enter your TPN DataWeb login details to enable this integration.</p>
              <FormField>
                <TextField
                  inputComponent={
                    <TextInput
                      id="tpn-username"
                      type={TextFieldTypes.text}
                      name="username"
                      autoComplete="off"
                      value={values.username}
                      onChange={onFieldChange}
                    />
                  }
                  label="Username"
                  error={visibleErrors.username}
                />
              </FormField>
              <FormField>
                <TextField
                  inputComponent={
                    <TextInput
                      id="tpn-password"
                      type={TextFieldTypes.password}
                      name="password"
                      autoComplete="new-password"
                      value={values.password}
                      onChange={onFieldChange}
                    />
                  }
                  label="Password"
                  error={visibleErrors.password}
                />
              </FormField>
              {saveError && <p className={styles.message}>{saveError}</p>}
            </DimmerLoader>
          </Modal.Body>
        )
      }}
    </Formik>
  )

  const renderConfirmDisconnect = () => (
    <Modal.Body
      actions={
        isRemoving
          ? []
          : [
              { label: 'Disable', onClick: () => dispatch(tpnApiEvents.removeTpnSettings_request()) },
              { label: 'Close', onClick: handleClose },
            ]
      }
    >
      <DimmerLoader loading={isRemoving} fadeStyle="flat" bgColor="white">
        <div className={styles.confirm}>
          <p className={styles.copy}>
            Disable this integration? We'll stop sending your data to TPN, but you can still download the report and
            upload it yourself.
          </p>
        </div>
      </DimmerLoader>
    </Modal.Body>
  )

  return (
    <Modal isOpen={isOpen} onClose={handleClose} width="md" padding="sm" showClose={!busy}>
      <Modal.Header title="TPN" />
      {view === 'connect' ? renderConnect() : renderConfirmDisconnect()}
    </Modal>
  )
}

export default TpnSettingsModal
